'use client';
import { useSession } from 'next-auth/react';
import { useCallback, useRef, useState } from 'react';

import { useProductLists } from './ProductListsContext';
import { findSaveForLaterList } from './productListsHelpers';
import {
  saveForLaterFailure,
  saveProductForLater,
  type OnSaveForLater,
  type SaveForLaterResult,
} from './saveForLater';
import { createDeferred, type Deferred } from './useProductMembership';

const SETTLE_TIMEOUT_MS = 5000;

export type UseSaveForLater = {
  onSaveForLater: OnSaveForLater;
  isSaving: boolean;
};

export function useSaveForLater(): UseSaveForLater {
  const { status } = useSession();
  const {
    personalLists,
    isProductListsEnabled,
    isProductListsSettled,
    fetchProductMembership,
    refreshPersonalLists,
    refreshProductMembership,
    commitListChanges,
  } = useProductLists();
  const [pendingCount, setPendingCount] = useState(0);

  // The mini-cart receives the function once, at initialisation, so it must
  // stay stable and read the latest provider state at call time.
  const latest = {
    status,
    personalLists,
    isProductListsEnabled,
    fetchProductMembership,
    refreshPersonalLists,
    refreshProductMembership,
    commitListChanges,
  };
  const latestRef = useRef(latest);
  latestRef.current = latest;

  // A click during hydration must not be answered from the pre-session state.
  const settledRef = useRef<Deferred | null>(null);
  const settledGate = (settledRef.current ??= createDeferred());
  if (isProductListsSettled && !settledGate.settled) {
    settledGate.settled = true;
    settledGate.resolve();
  }

  // A concurrent duplicate add fails with a 500 that an eventually consistent
  // lookup may not explain yet, so same-product calls share one save.
  const inflightRef = useRef(new Map<string, Promise<SaveForLaterResult>>());

  const runSave = useCallback(async (productId: string) => {
    setPendingCount((count) => count + 1);
    try {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const settled = await Promise.race([
        settledRef.current!.promise.then(() => true),
        new Promise<boolean>((res) => {
          timer = setTimeout(() => res(false), SETTLE_TIMEOUT_MS);
        }),
      ]).finally(() => clearTimeout(timer));
      if (!settled) return saveForLaterFailure('UNKNOWN');

      const latest = latestRef.current;
      if (latest.status === 'unauthenticated') {
        return saveForLaterFailure('UNAUTHENTICATED');
      }
      if (!latest.isProductListsEnabled) {
        return saveForLaterFailure('LIST_UNAVAILABLE');
      }

      return await saveProductForLater(productId, {
        // The provider's lists may still be loading; `/api/lists` also
        // provisions the list server-side on first read.
        resolveList: async () =>
          findSaveForLaterList(latest.personalLists) ??
          findSaveForLaterList((await latest.refreshPersonalLists()) ?? []),
        // Provider state can change mid-save (org settling, list discovery).
        readMembership: (id) => latestRef.current.fetchProductMembership(id),
        lookupMembership: (id) =>
          latestRef.current.refreshProductMembership(id),
        commit: (params) => latestRef.current.commitListChanges(params),
      });
    } catch (error) {
      // The consumer is promised a result, never a rejection.
      console.error('Error saving product for later:', error);
      return saveForLaterFailure('UNKNOWN');
    } finally {
      setPendingCount((count) => count - 1);
    }
  }, []);

  const onSaveForLater = useCallback<OnSaveForLater>(
    ({ productId }) => {
      const inflight = inflightRef.current.get(productId);
      if (inflight) return inflight;
      const request = runSave(productId).finally(() => {
        inflightRef.current.delete(productId);
      });
      inflightRef.current.set(productId, request);
      return request;
    },
    [runSave]
  );

  return { onSaveForLater, isSaving: pendingCount > 0 };
}
